/**
 * Keep body-appended ECharts tooltips from sticking after the pointer leaves
 * the chart (and after outside click / scroll / drag-select).
 */

const liveCharts = new Set()
let documentListenersAttached = false
let postInitInstalled = false
/** True while primary pointer is down on a live chart (brush / drag). */
let tipsSuppressedForDrag = false
/**
 * Touch press on a chart that has not moved far enough to count as a drag.
 * Taps must not suppress tips: touch has no hover, so the tap is the only trigger.
 * @type {{ chart: Object, x: number, y: number } | null}
 */
let pendingTouchPress = null

/** Touch movement (px) before a press on a chart is treated as a drag. */
const TOUCH_DRAG_THRESHOLD_PX = 10

function isChartAlive(chart) {
  return Boolean(
    chart
      && typeof chart.dispatchAction === 'function'
      && !(typeof chart.isDisposed === 'function' && chart.isDisposed()),
  )
}

/**
 * @param {Object} chart ECharts instance
 * @returns {HTMLElement | null}
 */
function getTooltipHtmlEl(chart) {
  const views = chart?._componentsViews
  if (!Array.isArray(views)) return null

  const tooltipView = views.find((view) => view?.type === 'tooltip')
  const el = tooltipView?._tooltipContent?.el
  return el instanceof HTMLElement ? el : null
}

function hideTip(chart) {
  if (!isChartAlive(chart)) return

  chart.dispatchAction({ type: 'hideTip' })
}

/**
 * Instantly hide the tip DOM, then sync ECharts state.
 * Avoids tips sliding/chasing the pointer during scroll or drag-select.
 *
 * @param {Object} chart ECharts instance
 */
export function hardHideEchartsTip(chart) {
  hardHideTip(chart)
}

function hardHideTip(chart) {
  if (!isChartAlive(chart)) return

  const el = getTooltipHtmlEl(chart)
  if (el?.style) {
    // Kill CSS transition so left/top/opacity do not animate during dismiss.
    el.style.transition = 'none'
    el.style.display = 'none'
    el.style.visibility = 'hidden'
    el.style.opacity = '0'
  }

  chart.dispatchAction({ type: 'hideTip' })
}

function forEachLiveChart(fn) {
  for (const chart of [...liveCharts]) {
    if (!isChartAlive(chart)) {
      liveCharts.delete(chart)
      continue
    }

    fn(chart)
  }
}

function isPrimaryPointerDown(event) {
  // Mouse left / touch / pen contact. Ignore right/middle.
  if (typeof event?.button === 'number' && event.button !== 0) return false
  return true
}

function onDocumentPointerDown(event) {
  if (!isPrimaryPointerDown(event)) return

  const target = event.target
  const isTouch = event.pointerType === 'touch'
  let hitLiveChart = false
  pendingTouchPress = null

  forEachLiveChart((chart) => {
    const dom = chart.getDom?.()
    if (dom?.contains?.(target)) {
      hitLiveChart = true

      if (isTouch) {
        pendingTouchPress = { chart, x: event.clientX, y: event.clientY }
        return
      }

      hardHideTip(chart)
      return
    }

    hideTip(chart)
  })

  tipsSuppressedForDrag = hitLiveChart && !isTouch
}

function onDocumentPointerMove(event) {
  if (!pendingTouchPress || event.pointerType !== 'touch') return

  const dx = event.clientX - pendingTouchPress.x
  const dy = event.clientY - pendingTouchPress.y

  if (Math.hypot(dx, dy) < TOUCH_DRAG_THRESHOLD_PX) return

  const { chart } = pendingTouchPress
  pendingTouchPress = null
  tipsSuppressedForDrag = true
  hardHideTip(chart)
}

function onDocumentPointerUp() {
  tipsSuppressedForDrag = false
  pendingTouchPress = null
}

function onDocumentScroll() {
  forEachLiveChart(hardHideTip)
}

function ensureDocumentListeners() {
  if (documentListenersAttached || typeof document === 'undefined') return

  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  document.addEventListener('pointermove', onDocumentPointerMove, true)
  document.addEventListener('pointerup', onDocumentPointerUp, true)
  document.addEventListener('pointercancel', onDocumentPointerUp, true)
  document.addEventListener('scroll', onDocumentScroll, true)
  documentListenersAttached = true
}

function removeDocumentListeners() {
  if (!documentListenersAttached || typeof document === 'undefined') return

  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  document.removeEventListener('pointermove', onDocumentPointerMove, true)
  document.removeEventListener('pointerup', onDocumentPointerUp, true)
  document.removeEventListener('pointercancel', onDocumentPointerUp, true)
  document.removeEventListener('scroll', onDocumentScroll, true)
  documentListenersAttached = false
}

/**
 * True while a primary pointer is held down on a live chart.
 * Used to skip tip repositioning during brush / drag-select.
 *
 * @returns {boolean}
 */
export function areEchartsTipsSuppressedForDrag() {
  return tipsSuppressedForDrag
}

/**
 * Bind hide-tip handlers for one chart instance.
 *
 * @param {Object} chart ECharts instance
 * @returns {() => void} teardown
 */
export function setupHideTooltipWhenPointerLeaves(chart) {
  if (!isChartAlive(chart)) return () => {}

  const hide = () => hideTip(chart)
  const suppressShowDuringDrag = () => {
    if (tipsSuppressedForDrag) hardHideTip(chart)
  }

  const zr = chart.getZr?.()
  zr?.on?.('globalout', hide)

  const dom = chart.getDom?.()
  dom?.addEventListener?.('mouseleave', hide)

  chart.on?.('showTip', suppressShowDuringDrag)

  liveCharts.add(chart)
  ensureDocumentListeners()

  return () => {
    zr?.off?.('globalout', hide)
    dom?.removeEventListener?.('mouseleave', hide)
    chart.off?.('showTip', suppressShowDuringDrag)
    liveCharts.delete(chart)
  }
}

/**
 * Register a post-init hook so every ECharts instance gets leave/outside/scroll
 * tooltip dismiss. Safe to call once at app boot (e.g. echarts-init.js).
 *
 * @param {Function} registerPostInit from echarts/core
 */
export function installHideEchartsTooltipOnLeave(registerPostInit) {
  if (postInitInstalled) return

  if (typeof registerPostInit !== 'function') {
    throw new Error(
      'installHideEchartsTooltipOnLeave requires registerPostInit',
    )
  }

  postInitInstalled = true
  registerPostInit((chart) => {
    setupHideTooltipWhenPointerLeaves(chart)
  })
}

/** @internal */
export const testables = {
  getLiveChartCount: () => liveCharts.size,
  isDocumentListenersAttached: () => documentListenersAttached,
  isPostInitInstalled: () => postInitInstalled,
  areTipsSuppressedForDrag: () => tipsSuppressedForDrag,
  reset() {
    liveCharts.clear()
    tipsSuppressedForDrag = false
    pendingTouchPress = null
    removeDocumentListeners()
    postInitInstalled = false
  },
}
